/**
 * EmployeeIdentityCard — the professional Growth Profile / CV surface on the employee
 * workspace (DIGEMP-GROWTH-PROFILE-FRONTEND-023). The page header already presents who the
 * employee is (avatar, name, role, status); this card does not repeat that identity block.
 *
 * Two kinds of truth are kept visually apart and never blended:
 *  - "מוצהר" (declared): role, specialties and declared expertise as configured for the employee.
 *    A declaration is not a measurement and grants nothing.
 *  - "נמדד" (evaluated): proficiency from the existing audited evaluation endpoint, which is
 *    admin-gated — a refusal degrades to an explicit unknown, never to a number.
 *
 * Growth-specific fields (declared expertise, learning goals, known limits, CV summary) are read
 * from the additive Growth Profile payload when the backend provides it; absent fields render as
 * an explicit unknown marker ("לא ידוע" / "טרם נמדד") instead of an invented value. When neither
 * the workspace nor the agent-detail payload carries the profile, the component reads the
 * canonical GET /api/agents/:id/growth-profile contract itself (same agents_view gate, read-only)
 * rather than rendering "not supplied" for a server that does supply it.
 */
import { useQuery } from "@tanstack/react-query";
import { agentsApi, skillsApi } from "../services/api";

const UNKNOWN_GOALS = "לא ידוע — יעדי למידה טרם סופקו על-ידי השרת";
const UNKNOWN_LIMITS = "לא ידוע — מגבלות ידועות טרם סופקו על-ידי השרת";
const NO_GOALS = "לא הוגדרו יעדי למידה";
const NO_LIMITS = "לא תועדו מגבלות ידועות";
const LOADING_GROWTH = "טוען את פרופיל העובד…";
const FAILED_GROWTH = "לא ניתן לטעון את פרופיל העובד — הנתונים אינם זמינים (לא ידוע)";

const GOAL_STATUS_HE = { open: "פתוח", acknowledged: "אושר" };

const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);
const numberOrNull = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

const firstText = (item, keys) => {
  if (typeof item === "string") return text(item);
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const found = text(item[key]);
    if (found) return found;
  }
  return null;
};

const hasField = (raw, key) => raw[key] !== undefined && raw[key] !== null;

function normalizeItemList(raw, key, textKeys, extraKeys = [], extraLabels = {}) {
  if (!hasField(raw, key)) return null;
  if (!Array.isArray(raw[key])) return null;
  return raw[key]
    .map((item) => {
      const value = firstText(item, textKeys);
      if (!value) return null;
      let extra = null;
      if (item && typeof item === "object") {
        const rawExtra = extraKeys.map((k) => text(item[k])).find(Boolean) || null;
        extra = rawExtra ? extraLabels[rawExtra] || rawExtra : null;
        if (extra === value) extra = null;
      }
      return { text: value, extra };
    })
    .filter(Boolean);
}

// The canonical GET /growth-profile shape (backend/services/growth_profile.py) groups declared
// facts under `declared`, limits under an object and proficiency under an aggregate. A prior
// client-only fixture used flat lists; both are read here so the card cannot show "no limits"
// for a payload that carries a full policy envelope.
function canonicalDeclared(raw) {
  const declared = raw.declared;
  if (!declared || typeof declared !== "object" || Array.isArray(declared)) return null;
  const items = (Array.isArray(declared.specialties) ? declared.specialties : [])
    .map(text)
    .filter(Boolean)
    .map((value) => ({ text: value, extra: null }));
  const statement = text(declared.expertise_statement);
  if (statement) items.push({ text: statement, extra: "הצהרת התמחות" });
  return items;
}

function canonicalLimitItems(limits) {
  if (!limits || typeof limits !== "object" || Array.isArray(limits)) return null;
  const items = [];
  const tier = text(limits.permission_tier);
  if (tier) items.push({ text: `רמת הרשאה: ${tier}`, extra: text(limits.permission_tier_meaning) });
  const autonomy = text(limits.autonomy_level);
  if (autonomy) items.push({ text: `רמת אוטונומיה: ${autonomy}`, extra: text(limits.autonomy_level_meaning) });
  if (limits.is_enabled === false) items.push({ text: "העובד מושבת — אינו מקבל עבודה חדשה", extra: null });
  if (Array.isArray(limits.allowed_tools_restriction)) {
    const restricted = limits.allowed_tools_restriction.map(text).filter(Boolean);
    if (restricted.length) items.push({ text: "מוגבל לרשימת כלים", extra: restricted.join(", ") });
  }
  const approvalTools = (Array.isArray(limits.approval_required_tools) ? limits.approval_required_tools : []).map(text).filter(Boolean);
  if (approvalTools.length) {
    items.push({ text: `${approvalTools.length} כלים דורשים אישור אדם לפני ביצוע`, extra: approvalTools.join(", ") });
  }
  const strongTools = (Array.isArray(limits.strong_approval_tools) ? limits.strong_approval_tools : []).map(text).filter(Boolean);
  if (strongTools.length) {
    items.push({ text: `${strongTools.length} כלים דורשים אישור חזק`, extra: strongTools.join(", ") });
  }
  return items;
}

function proficiencyItems(raw) {
  if (!Array.isArray(raw)) return null;
  return raw
    .map((item) => {
      const label = firstText(item, ["area", "category", "skill", "name"]);
      if (!label) return null;
      let pct = null;
      if (typeof item?.score === "number") pct = item.score;
      else if (typeof item?.completion_rate === "number") pct = item.completion_rate * 100;
      const evidence = [item?.total, item?.runs, item?.evidence_count].find((v) => typeof v === "number");
      return {
        label,
        pct: pct == null ? null : Math.min(100, Math.max(0, Math.round(pct))),
        evidence: evidence === undefined ? null : evidence,
      };
    })
    .filter(Boolean);
}

function normalizeProficiency(raw) {
  const value = raw.evaluated_proficiency;
  if (Array.isArray(value)) return { items: proficiencyItems(value), measured: null };
  if (!value || typeof value !== "object") return null;
  const evidence = value.evidence && typeof value.evidence === "object" ? value.evidence : {};
  return {
    items: proficiencyItems(value.by_category),
    hasOutcomeEvidence: value.has_outcome_evidence === true,
    overallScore: numberOrNull(value.overall_score),
    resolutionRate: numberOrNull(value.resolution_rate),
    totalRuns: numberOrNull(evidence.total_runs),
    completedRuns: numberOrNull(evidence.completed_runs),
    failedRuns: numberOrNull(evidence.failed_runs),
    pendingRuns: numberOrNull(evidence.pending_runs),
    evaluatedAt: text(value.evaluated_at),
  };
}

/**
 * Normalizes the additive Growth Profile payload into display-ready lists. Returns null when no
 * payload is present at all; a per-field null then means "field not supplied by the server" (shown
 * as unknown), while an empty array means the server explicitly reported "none".
 */
export function normalizeGrowthProfile(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const flatDeclared = normalizeItemList(raw, "declared_expertise", ["area", "skill", "name", "title", "text", "expertise"], ["note", "evidence", "detail", "source"]);
  const flatLimits = normalizeItemList(raw, "known_limits", ["limit", "text", "title", "description"], ["source", "note"]);
  return {
    role: text(raw.role_title) || text(raw.role) || text(raw.identity?.role_title),
    declaredExpertise: flatDeclared ?? canonicalDeclared(raw),
    learningGoals: normalizeItemList(
      raw,
      "learning_goals",
      ["goal", "topic", "title", "text", "label"],
      ["status", "state", "target", "note"],
      GOAL_STATUS_HE,
    ),
    knownLimits: flatLimits ?? canonicalLimitItems(raw.known_limits),
    cvSummary: text(raw.cv_summary) || text(raw.summary) || text(raw.cv_summary?.text),
    evaluatedProficiency: normalizeProficiency(raw),
  };
}

function FieldList({ items, emptyText, unknownText }) {
  if (items == null) return <p className="text-sm text-gray-400 italic">{unknownText}</p>;
  if (!items.length) return <p className="text-sm text-gray-400 italic">{emptyText}</p>;
  return (
    <ul className="space-y-1 text-sm text-gray-700">
      {items.map((item, i) => (
        <li key={`${item.text}-${i}`} className="flex gap-1.5">
          <span aria-hidden="true" className="text-gray-400">•</span>
          <span>
            {item.text}
            {item.extra && <span className="block text-[11px] text-gray-400">{item.extra}</span>}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ProficiencyBar({ label, pct, evidence }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-600 w-28 truncate" title={label}>{label}</span>
      {pct == null ? (
        <span className="text-xs text-gray-400">ללא ציון מדיד</span>
      ) : (
        <>
          <div className="flex-1 bg-white rounded-full h-2 overflow-hidden border border-blue-100">
            <div className={`h-2 rounded-full ${pct >= 70 ? "bg-blue-500" : pct >= 40 ? "bg-blue-400" : "bg-blue-300"}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-medium text-blue-700 w-8 text-left">{pct}</span>
        </>
      )}
      {evidence != null && <span className="text-[10px] text-gray-400 whitespace-nowrap">{evidence} ראיות</span>}
    </div>
  );
}

/**
 * The evaluation report carries its own evidence basis: overall_score and resolution_rate are null
 * until a run has finished, so a report without the flag is "not measured" rather than a number.
 */
const measuredReport = (ev) => (ev && ev.has_outcome_evidence === true ? ev : null);

export default function EmployeeIdentityCard({ agent, growthProfile, agentId }) {
  // The workspace and agent-detail payloads do not always carry the growth profile. The canonical
  // read contract is fetched only in that gap, under the same agents_view gate as the workspace;
  // a provided payload always wins and is never overwritten by this query.
  const needsGrowthFetch = !!agentId && growthProfile == null;
  const {
    data: fetchedGrowth,
    isLoading: growthLoading,
    isError: growthFailed,
  } = useQuery({
    queryKey: ["agent-growth-profile", agentId],
    queryFn: () => agentsApi.growthProfile(agentId),
    staleTime: 120_000,
    enabled: needsGrowthFetch,
  });

  const { data: evaluation, isLoading: evaluationLoading, isError: evaluationFailed, error: evaluationError } = useQuery({
    queryKey: ["agent-evaluation", agentId],
    queryFn: () => skillsApi.agentEvaluation(agentId),
    staleTime: 120_000,
    enabled: !!agentId,
  });

  if (!agent) return null;

  const profileRaw = growthProfile != null ? growthProfile : fetchedGrowth;
  const profile = normalizeGrowthProfile(profileRaw);
  const role = (profile && profile.role) || text(agent.role_title);
  const specialties = (Array.isArray(agent.specialties) ? agent.specialties : []).map(text).filter(Boolean);
  // The declared side may arrive from the agent row (specialties) and from the contract; the same
  // declared specialty must not be drawn twice.
  const declaredExtra = ((profile && profile.declaredExpertise) || [])
    .filter((item) => item.extra || !specialties.includes(item.text));
  const declaredEmpty = specialties.length === 0 && declaredExtra.length === 0;

  const contract = profile && profile.evaluatedProficiency;
  const contractSettled = contract && typeof contract.hasOutcomeEvidence === "boolean";
  const contractMeasured = contractSettled && contract.hasOutcomeEvidence
    ? {
        overall_score: contract.overallScore,
        resolution_rate: contract.resolutionRate,
        total_runs: contract.totalRuns,
        completed_runs: contract.completedRuns,
        failed_runs: contract.failedRuns,
      }
    : null;
  // The growth contract is the non-admin-readable copy of the same deterministic evaluation. When
  // it states the evidence verdict, that verdict owns the card; a caller-supplied flat fixture or
  // the admin-only evaluation endpoint is the fallback.
  const measured = contractSettled ? contractMeasured : measuredReport(evaluation);
  const measuredVisual = measured && typeof measured.overall_score === "number";
  const contractItems = (contract && contract.items) || null;

  let evaluatedBody;
  if (measuredVisual) {
    const rate = typeof measured.resolution_rate === "number"
      ? `${Math.round(measured.resolution_rate * 100)}%`
      : "לא ידוע";
    const evidenceCount = typeof measured.total_runs === "number" ? measured.total_runs : "לא ידוע";
    evaluatedBody = (
      <div className="space-y-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-700">{Math.round(measured.overall_score)}</div>
            <div className="text-[11px] text-gray-400">ציון כולל</div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center text-xs flex-1">
            {[
              { label: "שיעור פתרון", value: rate },
              { label: "הושלמו", value: measured.completed_runs ?? "לא ידוע" },
              { label: "נכשלו", value: measured.failed_runs ?? "לא ידוע" },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white rounded-lg py-2 border border-blue-100">
                <div className="font-semibold text-gray-700">{value}</div>
                <div className="text-gray-400 mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </div>
        {contractItems && contractItems.length > 0 && (
          <div className="space-y-1.5">
            {contractItems.map((item, i) => (
              <ProficiencyBar key={`${item.label}-${i}`} label={item.label} pct={item.pct} evidence={item.evidence} />
            ))}
          </div>
        )}
        {/* The contract carries per-category bars; the admin evaluation does not repeat that
            breakdown here, where the page's "הערכת מיומנויות" section owns it. */}
        <p className="text-[11px] text-gray-400">{evidenceCount} ריצות נמדדו · מבוסס ריצות שהסתיימו</p>
      </div>
    );
  } else if (contractItems && contractItems.length > 0) {
    evaluatedBody = (
      <div className="space-y-1.5">
        {contractItems.map((item, i) => (
          <ProficiencyBar key={`${item.label}-${i}`} label={item.label} pct={item.pct} evidence={item.evidence} />
        ))}
      </div>
    );
  } else if (contractSettled && !contract.hasOutcomeEvidence) {
    evaluatedBody = <p className="text-sm text-gray-400 italic">טרם נמדד — אין עדיין ריצות שהסתיימו להערכה</p>;
  } else if (evaluationLoading) {
    // Before the measurement settles, don't claim "not measured" — that is a different fact.
    evaluatedBody = <p className="text-sm text-gray-400 italic">טוען נתוני הערכה…</p>;
  } else if (evaluationFailed) {
    const refused = evaluationError?.response?.status === 403;
    evaluatedBody = (
      <p className="text-sm text-gray-400 italic">
        {refused
          ? "אין הרשאה לצפות בנתוני ההערכה — הנתונים מוצגים למנהלים בלבד (לא ידוע)"
          : "לא ניתן לטעון את נתוני ההערכה (לא ידוע)"}
      </p>
    );
  } else {
    evaluatedBody = <p className="text-sm text-gray-400 italic">טרם נמדד — אין עדיין ריצות שהסתיימו להערכה</p>;
  }

  const measuredLine = measuredVisual
    ? [
        typeof measured.total_runs === "number" ? `${measured.total_runs} ריצות` : null,
        typeof measured.resolution_rate === "number" ? `שיעור פתרון ${Math.round(measured.resolution_rate * 100)}%` : null,
      ].filter(Boolean).join(" · ") || null
    : null;
  const measurementLoading = !measuredVisual && !evaluationFailed &&
    ((evaluationLoading && !contractSettled) || (needsGrowthFetch && growthLoading));
  const summary = (profile && profile.cvSummary) || [
    `${agent.name}${role ? ` — ${role}` : ""}.`,
    declaredEmpty
      ? "לא הוצהרו תחומי התמחות."
      : `התמחות מוצהרת: ${[...specialties, ...declaredExtra.map((d) => d.text)].join(", ")}.`,
    measuredLine
      ? `מיומנות נמדדת: ${measuredLine}.`
      : measurementLoading ? "מיומנות נמדדת: טוען נתונים…" : "מיומנות נמדדת: טרם נמדדה.",
  ].join(" ");

  const goalsUnknownText = needsGrowthFetch && growthLoading
    ? LOADING_GROWTH
    : needsGrowthFetch && growthFailed ? FAILED_GROWTH : UNKNOWN_GOALS;
  const limitsUnknownText = needsGrowthFetch && growthLoading
    ? LOADING_GROWTH
    : needsGrowthFetch && growthFailed ? FAILED_GROWTH : UNKNOWN_LIMITS;

  return (
    <section aria-label="פרופיל מקצועי" className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-gray-700">פרופיל מקצועי · CV</h3>
        <span className="text-[11px] text-gray-400">ראיות מוצהרות ונמדדות בלבד · הפרופיל אינו מעניק הרשאות</span>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
        <p className="text-[11px] text-gray-400 mb-1">תקציר CV</p>
        <p className="text-sm text-gray-700 leading-6">{summary}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div data-testid="declared-expertise" className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-violet-800">התמחות מוצהרת</h3>
            <span className="text-[10px] rounded-full border border-violet-200 bg-violet-100 text-violet-700 px-1.5 py-0.5">מוצהר</span>
          </div>
          <p className="text-[11px] text-violet-700/80">מהגדרת העובד — אינו מדד ביצועים ואינו מעניק הרשאות</p>
          {declaredEmpty ? (
            <p className="text-sm text-gray-400 italic">לא הוצהרו תחומי התמחות</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {specialties.map((s, i) => (
                <span key={`s-${s}-${i}`} className="text-[11px] px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">{s}</span>
              ))}
              {declaredExtra.map((d, i) => (
                <span key={`d-${d.text}-${i}`} className="text-[11px] px-2 py-0.5 rounded-full bg-white text-violet-700 border border-violet-200">
                  {d.text}{d.extra ? ` · ${d.extra}` : ""}
                </span>
              ))}
            </div>
          )}
        </div>

        <div data-testid="evaluated-proficiency" className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-blue-800">מיומנות נמדדת</h3>
            <span className="text-[10px] rounded-full border border-blue-200 bg-blue-100 text-blue-700 px-1.5 py-0.5">נמדד</span>
          </div>
          <p className="text-[11px] text-blue-800/80">מבוסס ריצות שהסתיימו — ראיות נמדדות מהמערכת</p>
          {evaluatedBody}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">יעדי למידה</h3>
          <FieldList items={profile ? profile.learningGoals : null} emptyText={NO_GOALS} unknownText={goalsUnknownText} />
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">מגבלות ידועות</h3>
          <FieldList items={profile ? profile.knownLimits : null} emptyText={NO_LIMITS} unknownText={limitsUnknownText} />
        </div>
      </div>
    </section>
  );
}
